import { Box, useLayoutContext, useTheme } from "arkh";
import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorNavigationProp } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { onlyUnique } from "../../../utils";
import { PLUCategoryIdentifiers } from "../PLUCategoryIdentifiers";
import { PLUCategoryCreateMutation } from "./__generated__/PLUCategoryCreateMutation.graphql";
import { PLUCategoryCreateQuery } from "./__generated__/PLUCategoryCreateQuery.graphql";
import { useNavigationHeaderOptions } from "../../../common/navigationHeaderOptions";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"PLUCategoryCreate">;
};

export const PLUCategoryCreate = withWebModalStack(PLUCategoryCreateC);
function PLUCategoryCreateC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerPLUCategoryCreate fetchKey={fetchKey} {...props} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerPLUCategoryCreate(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<PLUCategoryCreateQuery>(
    graphql`
      query PLUCategoryCreateQuery {
        pluCategories {
          id
          color
        }
      }
    `,
    {},
    { fetchKey: props.fetchKey }
  );

  const usedColors = useMemo(() => {
    return data.pluCategories.map((category) => category.color);
  }, [data.pluCategories]);

  const theme = useTheme();
  const layout = useLayoutContext();

  const [color, setColor] = useState<string>(
    theme.colorScheme.filter((c) => !usedColors.includes(c))[0] ?? theme.primary
  );
  const [title, setTitle] = useState<string>("");
  const [createPLUCategory, isInFlight] =
    useMutation<PLUCategoryCreateMutation>(graphql`
      mutation PLUCategoryCreateMutation($color: String!, $title: String!) {
        createPLUCategory(color: $color, title: $title) {
          pluCategories {
            id
            title
            color
          }
        }
      }
    `);

  const onCreate = useCallback(() => {
    createPLUCategory({
      variables: {
        color,
        title,
      },
      onCompleted: () => props.navigation.goBack(),
    });
  }, [color, createPLUCategory, props.navigation, title]);

  const disabled = title.length === 0;
  const getHeaderOptions = useNavigationHeaderOptions();

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getHeaderOptions({
        type: "CREATE",
        onSave: onCreate,
        saveLoading: isInFlight,
        disabled,
        onCancel: props.navigation.goBack,
        title: "Lägg till kategori",
      })
    );
  }, [disabled, getHeaderOptions, isInFlight, onCreate, props.navigation]);

  return (
    <Box style={{ flex: 1 }}>
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="interactive"
        contentContainerStyle={{
          paddingVertical: layout.space.medium,
        }}
      >
        <PLUCategoryIdentifiers
          autoFocus
          color={color}
          setColor={setColor}
          title={title}
          setTitle={setTitle}
          usedColors={usedColors.filter(onlyUnique)}
        />
      </KeyboardAwareScrollView>
    </Box>
  );
}
